/**
 * The one place a network request is made.
 *
 * Two rules that exist because breaking them costs a support ticket:
 *  1. Every non-2xx becomes an ApiError carrying `status` and the server's
 *     `code`. Screens branch on the code, never on a substring of a message.
 *  2. `tenantId` travels in a header, not a query string, so it cannot be
 *     dropped by a caller who forgot it. A request without a tenant is a bug
 *     the API must reject, not one the UI should paper over.
 */
import { mockFetch } from '@/mocks/mock-api'

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api'
// Real API by default. Mock mode is now an explicit opt-in for isolated UI work.
export const USE_MOCKS = (import.meta.env.VITE_USE_MOCKS ?? '0') === '1'

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }

  /** True for the failures a retry could plausibly fix. */
  get isTransient() {
    return this.status === 0 || this.status === 429 || this.status >= 500
  }
}

let authToken: string | null = null
let tenantId: string | null = null

export function setAuthContext(next: { token: string | null; tenantId: string | null }) {
  authToken = next.token
  tenantId = next.tenantId
}

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  query?: Record<string, string | number | boolean | undefined | null>
  body?: unknown
  signal?: AbortSignal
}

function buildUrl(path: string, query?: RequestOptions['query']) {
  const url = `${BASE_URL}${path.startsWith('/') ? path : `/${path}`}`
  if (!query) return url
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue
    params.set(key, String(value))
  }
  const qs = params.toString()
  return qs ? `${url}?${qs}` : url
}

/**
 * Leave the SPA to begin the provider redirect flow.
 *
 * `email` is sent for Microsoft because there is one Cognito identity provider
 * per CUSTOMER DIRECTORY — Cognito validates the id_token's issuer, and Entra
 * issues a per-tenant one even for a multi-tenant app registration. The API
 * resolves the email's domain to a directory server-side; the browser is never
 * told which domains are configured.
 */
export function beginSso(provider: 'microsoft' | 'google', returnTo = '/', email?: string) {
  if (USE_MOCKS) {
    const query = new URLSearchParams({ code: 'mock-code', returnTo })
    window.location.assign(`/auth/callback?${query}`)
    return
  }
  window.location.assign(buildUrl(`/auth/sso/${provider}/start`, { returnTo, email }))
}

/**
 * Obtain a fresh access token from the HttpOnly refresh cookie.
 *
 * Single-flight: a page that fires six queries at once must not send six
 * refreshes, because rotation invalidates the previous token and five of them
 * would then race to use a refresh token that has already been spent.
 */
let refreshInFlight: Promise<boolean> | null = null

export function refreshSession(): Promise<boolean> {
  refreshInFlight ??= (async () => {
    try {
      const response = USE_MOCKS
        ? await mockFetch(buildUrl('/auth/refresh'), { method: 'POST' })
        : await fetch(buildUrl('/auth/refresh'), {
            method: 'POST',
            credentials: 'include',
            headers: { Accept: 'application/json' },
          })
      if (!response.ok) return false
      const session = (await response.json()) as SessionRefresh
      onSessionRefreshed?.(session)
      return true
    } catch {
      return false
    } finally {
      // Cleared in a microtask so callers awaiting this same promise all see
      // the same result before a new attempt can start.
      queueMicrotask(() => {
        refreshInFlight = null
      })
    }
  })()
  return refreshInFlight
}

interface SessionRefresh {
  token: string
  expiresIn: number
  user: unknown
  tenant: unknown
}

let onSessionRefreshed: ((session: SessionRefresh) => void) | null = null
let onSessionLost: (() => void) | null = null

/** Wired once at start-up so this module never imports the session store. */
export function setSessionHandlers(handlers: {
  onRefreshed: (session: SessionRefresh) => void
  onLost: () => void
}) {
  onSessionRefreshed = handlers.onRefreshed
  onSessionLost = handlers.onLost
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await send<T>(path, options)
  return response
}

/** Endpoints that must never trigger a refresh-and-retry of themselves. */
const AUTH_PATHS = ['/auth/login', '/auth/refresh', '/auth/logout', '/auth/sso/exchange']

async function send<T>(path: string, options: RequestOptions, retried = false): Promise<T> {
  const { query, body, headers, ...rest } = options
  const url = buildUrl(path, query)

  const init: RequestInit = {
    // Refresh and authorization-flow credentials are HttpOnly cookies. This is
    // also required when the API is hosted on a sibling subdomain.
    credentials: 'include',
    ...rest,
    headers: {
      Accept: 'application/json',
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
      ...(tenantId ? { 'X-Sabha-Tenant': tenantId } : {}),
      ...headers,
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  }

  const response = USE_MOCKS ? await mockFetch(url, init) : await fetch(url, init)

  // A 401 on an ordinary call means the access token expired, not that the
  // person is signed out: the refresh cookie outlives it by thirty days. Try
  // once, then give up -- retrying a second time would loop against an API that
  // is rejecting a token it just issued.
  if (response.status === 401 && !retried && !AUTH_PATHS.includes(path)) {
    if (await refreshSession()) return send<T>(path, options, true)
    onSessionLost?.()
  }

  if (response.status === 204) return undefined as T

  const text = await response.text()
  const payload = text ? safeParse(text) : null

  if (!response.ok) {
    const problem = (payload ?? {}) as { code?: string; message?: string; details?: unknown }
    throw new ApiError(
      response.status,
      problem.code ?? `http_${response.status}`,
      problem.message ?? response.statusText ?? 'Request failed',
      problem.details,
    )
  }

  return payload as T
}

/**
 * Binary variant of `send`, for endpoints that return a file rather than
 * JSON (the meeting export PDF). Shares the same auth headers and the same
 * expired-token refresh-and-retry -- the access token lives in memory and
 * travels as a header, so a plain `<a href>` pointed at the API would be
 * sent with none and answered 401.
 */
async function sendBlob(
  path: string,
  options: RequestOptions,
  retried = false,
): Promise<Blob> {
  // No `body`: every caller of `sendBlob` is a GET (see `api.getBlob`), and
  // `RequestOptions.body` is untyped (`unknown`), which is not assignable to
  // `RequestInit.body` -- spreading it in the way `send` spreads its own
  // (typed, JSON-stringified) body would is a type error here for nothing
  // any caller needs.
  const url = buildUrl(path, options.query)

  const init: RequestInit = {
    credentials: 'include',
    method: options.method,
    signal: options.signal,
    headers: {
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
      ...(tenantId ? { 'X-Sabha-Tenant': tenantId } : {}),
      ...options.headers,
    },
  }

  const response = USE_MOCKS ? await mockFetch(url, init) : await fetch(url, init)

  if (response.status === 401 && !retried) {
    if (await refreshSession()) return sendBlob(path, options, true)
    onSessionLost?.()
  }

  if (!response.ok) {
    const text = await response.text()
    const payload = text ? safeParse(text) : null
    const problem = (payload ?? {}) as { code?: string; message?: string; details?: unknown }
    throw new ApiError(
      response.status,
      problem.code ?? `http_${response.status}`,
      problem.message ?? response.statusText ?? 'Request failed',
      problem.details,
    )
  }

  return response.blob()
}

/**
 * Upload variant of `send`, for endpoints whose request body is raw bytes
 * rather than JSON (the "Record now" audio chunks). Same auth headers and the
 * same expired-token refresh-and-retry; `send` would `JSON.stringify` the body
 * and label it `application/json`, which turns audio into a string.
 */
async function sendBinary(
  path: string,
  body: ArrayBuffer,
  options: RequestOptions,
  retried = false,
): Promise<void> {
  const url = buildUrl(path, options.query)

  const init: RequestInit = {
    credentials: 'include',
    method: options.method,
    signal: options.signal,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/octet-stream',
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
      ...(tenantId ? { 'X-Sabha-Tenant': tenantId } : {}),
      ...options.headers,
    },
    body,
  }

  const response = USE_MOCKS ? await mockFetch(url, init) : await fetch(url, init)

  if (response.status === 401 && !retried) {
    if (await refreshSession()) return sendBinary(path, body, options, true)
    onSessionLost?.()
  }

  if (!response.ok) {
    const text = await response.text()
    const payload = text ? safeParse(text) : null
    const problem = (payload ?? {}) as { code?: string; message?: string; details?: unknown }
    throw new ApiError(
      response.status,
      problem.code ?? `http_${response.status}`,
      problem.message ?? response.statusText ?? 'Request failed',
      problem.details,
    )
  }
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text)
  } catch {
    return { message: text }
  }
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) => request<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PUT', body }),
  delete: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'DELETE' }),
  getBlob: (path: string, options?: RequestOptions) =>
    sendBlob(path, { ...options, method: 'GET' }),
  putBinary: (path: string, body: ArrayBuffer, options?: RequestOptions) =>
    sendBinary(path, body, { ...options, method: 'PUT' }),
}
